/**
 * Ultra-modern design-system primitives (frontend-adminclient / cabinet portal).
 * ---------------------------------------------------------------------------
 * A tiny, reusable set that gives every cabinet page the same modern, layered
 * look, mirroring the frontend-client redesign kit:
 *   - <GradientHeader> : colored/gradient page or section banner (icon+title+
 *                        subtitle+actions) with soft 3D depth + radial glow.
 *   - <GradientButton> : gradient CTA (wraps this portal's <Button>).
 *   - <Surface>        : card with layered/soft shadow for depth (raised /
 *                        glass / sunken / flat), optional hover elevation.
 *
 * All reuse the existing theme tokens (Tailwind palette + Aurora shadows) so
 * they stay consistent with light/dark + the color themes.
 */
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, type ButtonProps } from '@/components/ui/button'

// ── Shared gradient palette (reused by GradientHeader/Button + consumers) ────
export type GradientTone =
  | 'primary' | 'indigo' | 'blue' | 'violet' | 'purple' | 'cyan' | 'teal'
  | 'emerald' | 'amber' | 'orange' | 'rose' | 'red' | 'slate'

export const GRADIENT_CLASSES: Record<GradientTone, string> = {
  primary: 'from-primary via-primary to-primary/80',
  indigo:  'from-indigo-500 via-indigo-600 to-violet-700',
  blue:    'from-blue-500 via-blue-600 to-indigo-700',
  violet:  'from-violet-500 via-violet-600 to-purple-700',
  purple:  'from-purple-500 via-purple-600 to-fuchsia-700',
  cyan:    'from-cyan-500 via-cyan-600 to-blue-700',
  teal:    'from-teal-500 via-teal-600 to-emerald-700',
  emerald: 'from-emerald-500 via-emerald-600 to-teal-700',
  amber:   'from-amber-500 via-amber-600 to-orange-700',
  orange:  'from-orange-500 via-orange-600 to-red-600',
  rose:    'from-rose-500 via-rose-600 to-pink-700',
  red:     'from-red-500 via-red-600 to-rose-700',
  slate:   'from-slate-600 via-slate-700 to-slate-900',
}

// ── GradientHeader ───────────────────────────────────────────────────────────
export interface GradientHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: LucideIcon
  tone?: GradientTone
  /** Right-aligned actions (buttons, badges). */
  actions?: React.ReactNode
  /** Compact vertical padding. */
  compact?: boolean
}

export const GradientHeader = React.forwardRef<HTMLDivElement, GradientHeaderProps>(
  ({ title, subtitle, icon: Icon, tone = 'indigo', actions, compact, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-2xl text-white shadow-lg',
        'bg-gradient-to-br',
        GRADIENT_CLASSES[tone],
        compact ? 'px-4 py-3.5 sm:px-5' : 'px-5 py-5 sm:px-6 sm:py-6',
        className,
      )}
      {...rest}
    >
      {/* 3D depth: top-left radial highlight + bottom edge highlight */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_15%_10%,rgba(255,255,255,0.30)_0%,transparent_55%)]" />
      <div aria-hidden className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-white/25" />

      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {Icon && (
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm">
              <Icon className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className={cn('truncate font-semibold tracking-tight', compact ? 'text-lg' : 'text-xl sm:text-2xl')}>
              {title}
            </h1>
            {subtitle && (
              <p className="mt-0.5 truncate text-sm text-white/85">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  ),
)
GradientHeader.displayName = 'GradientHeader'

// ── GradientButton ───────────────────────────────────────────────────────────
export interface GradientButtonProps extends ButtonProps {
  tone?: GradientTone
}

export const GradientButton = React.forwardRef<HTMLButtonElement, GradientButtonProps>(
  ({ tone = 'indigo', className, variant: _variant, ...props }, ref) => (
    <Button
      ref={ref}
      variant="default"
      className={cn(
        'border-0 bg-gradient-to-br text-white shadow-md',
        GRADIENT_CLASSES[tone],
        'transition-all hover:brightness-110 hover:shadow-lg active:brightness-95',
        className,
      )}
      {...props}
    />
  ),
)
GradientButton.displayName = 'GradientButton'

// ── Surface (card with depth) ────────────────────────────────────────────────
export type SurfaceVariant = 'raised' | 'glass' | 'sunken' | 'flat'

const SURFACE_VARIANTS: Record<SurfaceVariant, string> = {
  raised:
    'bg-card text-card-foreground border border-border/50 ring-1 ring-black/[0.03] dark:ring-white/[0.04] ' +
    // Layered ambient + contact shadow → richer 3D depth (light)
    'shadow-[0_1px_1px_rgba(16,24,40,0.05),0_2px_4px_-1px_rgba(16,24,40,0.06),0_8px_16px_-4px_rgba(16,24,40,0.08),0_24px_48px_-12px_rgba(16,24,40,0.10)] ' +
    'dark:shadow-[0_1px_2px_rgba(0,0,0,0.40),0_4px_10px_-2px_rgba(0,0,0,0.45),0_16px_40px_-8px_rgba(0,0,0,0.55)]',
  glass:
    'bg-card/70 text-card-foreground border border-white/20 dark:border-white/10 backdrop-blur-xl ' +
    'shadow-[0_2px_8px_-2px_rgba(16,24,40,0.10),0_16px_40px_-12px_rgba(16,24,40,0.14)]',
  sunken:
    'bg-muted/50 text-card-foreground border border-border/50 shadow-inner',
  flat:
    'bg-card text-card-foreground border border-border/60',
}

export interface SurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SurfaceVariant
  /** Lift + deepen shadow on hover (great for clickable cards). */
  interactive?: boolean
  /** Colored left accent bar. Accepts a Tailwind bg-* class or CSS color. */
  accent?: string
  asChild?: boolean
}

export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(
  ({ variant = 'raised', interactive, accent, asChild, className, style, children, ...rest }, ref) => {
    const Comp: React.ElementType = asChild ? Slot : 'div'
    const accentIsClass = accent && accent.startsWith('bg-')
    const showSheen = variant === 'raised' || variant === 'glass'
    return (
      <Comp
        ref={ref}
        className={cn(
          'group/surface relative overflow-hidden rounded-2xl',
          SURFACE_VARIANTS[variant],
          interactive &&
            'cursor-pointer transition-all duration-300 ease-out will-change-transform ' +
            'hover:-translate-y-1 ' +
            // Deepened elevation on hover for a stronger 3D "lift"
            'hover:shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_16px_32px_-6px_rgba(16,24,40,0.16),0_36px_64px_-16px_rgba(16,24,40,0.18)] ' +
            'hover:border-border/80 hover:ring-black/[0.06] dark:hover:ring-white/[0.08] ' +
            'dark:hover:shadow-[0_8px_20px_-4px_rgba(0,0,0,0.55),0_28px_60px_-12px_rgba(0,0,0,0.7)] ' +
            // Press feedback
            'active:translate-y-0 active:scale-[0.995] active:transition-none ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          accent && 'pl-[3px]',
          className,
        )}
        style={
          accent && !accentIsClass ? { ...style, borderLeft: `3px solid ${accent}` } : style
        }
        {...rest}
      >
        {/* Top-edge highlight sheen → subtle glassy 3D bevel */}
        {showSheen && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent dark:via-white/10"
          />
        )}
        {accent && accentIsClass && (
          <span aria-hidden className={cn('absolute inset-y-0 left-0 w-[3px]', accent)} />
        )}
        {children}
      </Comp>
    )
  },
)
Surface.displayName = 'Surface'
